import { useRef, useState } from "react";
import {
  usePortal,
  FILE_LIMITS,
  type ClientDetail,
  type UpdateBrandingBody,
  type UpdateClientBody, type RefResolved } from "@app/portal-sdk";
import { Alert, Box, Button, FormControlLabel, SectionCard, Stack, Switch, TextField, Typography, MenuItem } from "@app/ui-kit";
import LinearProgress from "@mui/material/LinearProgress";
import Snackbar from "@mui/material/Snackbar";
import { uploadClientFile } from "../../lib/upload";
import { errorMessage, useApiQuery } from "../../lib/useApiQuery";
import { ClientSettingsSection } from "../../client/settings/ClientSettingsSection";

type Props = {
  client: ClientDetail;
  onChange: (client: ClientDetail) => void;
};

/** Overview: engagement status note, branding (logo, accent, tagline), features, SSO. */
export const OverviewTab = ({ client, onChange }: Props) => {
  const { api } = usePortal();
  const [statusNote, setStatusNote] = useState(client.statusNote ?? "");
  // SHL-3: the colour beside the note. Independent of it: either may be set without the other.
  const [healthCode, setHealthCode] = useState(client.healthCode ?? "");
  const healthOptions = useApiQuery<RefResolved>(() => api.request<RefResolved>(`/clients/${client.id}/reference?all=true`), [api, client.id]);
  const healthValues = healthOptions.data?.domains.find((d) => d.domain === "client_health")?.values.filter((v) => v.isActive) ?? [];
  const [accent, setAccent] = useState(client.brand.accent ?? "");
  const [tagline, setTagline] = useState(client.brand.tagline ?? "");
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const run = async (label: string, fn: () => Promise<ClientDetail>, done: string) => {
    setBusy(label);
    setError(null);
    try {
      onChange(await fn());
      setToast(done);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const saveStatusNote = () =>
    run(
      "note",
      () => {
        const body: UpdateClientBody = { statusNote: statusNote.trim() ? statusNote : null, healthCode: healthCode || null };
        return api.request<ClientDetail>(`/admin/clients/${client.id}`, { method: "PATCH", json: body });
      },
      "Engagement status saved"
    );

  const saveBranding = () =>
    run(
      "branding",
      () => {
        const body: UpdateBrandingBody = { accent: accent.trim() || undefined, tagline: tagline.trim() };
        return api.request<ClientDetail>(`/admin/clients/${client.id}/branding`, { method: "PUT", json: body });
      },
      "Branding saved"
    );

  const toggleDocuments = (enabled: boolean) =>
    run(
      "features",
      () => {
        const body: UpdateClientBody = { features: { ...client.features, documents: enabled } };
        return api.request<ClientDetail>(`/admin/clients/${client.id}`, { method: "PATCH", json: body });
      },
      enabled ? "Documents enabled" : "Documents hidden"
    );

  const uploadLogo = async (file: File) => {
    if (file.size > FILE_LIMITS.logoMaxBytes) {
      setError("Logo must be 512 KB or smaller");
      return;
    }
    if (!(FILE_LIMITS.logoContentTypes as readonly string[]).includes(file.type)) {
      setError("Logo must be an SVG or PNG");
      return;
    }
    await run(
      "logo",
      async () => {
        await uploadClientFile(api, client.id, file, "branding");
        return api.request<ClientDetail>(`/admin/clients/${client.id}`);
      },
      "Logo updated"
    );
  };

  const removeLogo = () =>
    run(
      "logo",
      () => api.request<ClientDetail>(`/admin/clients/${client.id}/logo`, { method: "DELETE" }),
      "Logo removed"
    );

  const accentValid = accent === "" || /^#[0-9a-fA-F]{6}$/.test(accent);

  return (
    <Stack spacing={4}>
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

      <SectionCard eyebrow="Client Home" title="Engagement status">
        <Stack spacing={2}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Shown at the top of the client's home page. The colour is the state at a glance; the note is the explanation. Either can be set without the other.
          </Typography>
          <TextField select size="small" label="Engagement health" value={healthCode} onChange={(event) => setHealthCode(event.target.value)} sx={{ maxWidth: 320 }} helperText="One of three. The client sees a stoplight with this word beside it." inputProps={{ "data-testid": "health-select" }}>
            <MenuItem value=""><em>Not set</em></MenuItem>
            {healthValues.map((v) => <MenuItem key={v.code} value={v.code}>{v.label}{v.description ? ` — ${v.description}` : ""}</MenuItem>)}
          </TextField>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            The note: where the engagement is and what happens next, in prose. Blank lines separate paragraphs.
          </Typography>
          <TextField
            multiline
            minRows={5}
            value={statusNote}
            onChange={(event) => setStatusNote(event.target.value)}
            placeholder="Diligence fieldwork is complete. The draft memo lands Friday; the readout with the investment committee is scheduled for the 18th."
            fullWidth
          />
          <Box>
            <Button variant="contained" onClick={() => void saveStatusNote()} disabled={busy !== null}>
              Save status
            </Button>
          </Box>
        </Stack>
      </SectionCard>

      <SectionCard eyebrow="Identity" title="Branding">
        <Stack spacing={3}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={3} alignItems={{ md: "center" }}>
            <Box
              sx={{
                width: 200,
                height: 88,
                border: "1.5px dashed",
                borderColor: "divider",
                borderRadius: "6px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "background.default",
                flexShrink: 0
              }}
            >
              {client.logoUrl ? (
                <Box component="img" src={client.logoUrl} alt={client.name} sx={{ maxHeight: 64, maxWidth: 180 }} />
              ) : (
                <Typography variant="caption" sx={{ color: "text.disabled" }}>
                  No logo
                </Typography>
              )}
            </Box>
            <Stack spacing={1}>
              <Stack direction="row" spacing={1.5}>
                <Button variant="outlined" onClick={() => fileInput.current?.click()} disabled={busy !== null}>
                  Upload logo
                </Button>
                {client.logoUrl && (
                  <Button variant="text" color="error" onClick={() => void removeLogo()} disabled={busy !== null}>
                    Remove
                  </Button>
                )}
              </Stack>
              <Typography variant="caption" sx={{ color: "text.disabled" }}>
                SVG or PNG, up to 512 KB. Shown on the client's home page and in the portal header.
              </Typography>
              <input
                ref={fileInput}
                type="file"
                accept=".svg,.png,image/svg+xml,image/png"
                hidden
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  event.target.value = "";
                  if (file) void uploadLogo(file);
                }}
              />
            </Stack>
          </Stack>
          {busy === "logo" && <LinearProgress color="secondary" />}
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <TextField
              label="Accent color"
              value={accent}
              onChange={(event) => setAccent(event.target.value)}
              placeholder="#1B98E0"
              error={!accentValid}
              helperText={accentValid ? "Optional. #RRGGBB" : "Use #RRGGBB"}
              InputProps={{
                startAdornment: (
                  <Box
                    sx={{
                      width: 18,
                      height: 18,
                      borderRadius: "3px",
                      mr: 1.5,
                      border: "1px solid",
                      borderColor: "divider",
                      bgcolor: accentValid && accent ? accent : "transparent"
                    }}
                  />
                )
              }}
              sx={{ maxWidth: 260 }}
            />
            <TextField
              label="Tagline"
              value={tagline}
              onChange={(event) => setTagline(event.target.value)}
              placeholder="Technology diligence, Series C"
              helperText="Optional. Shown under the client name."
              fullWidth
            />
          </Stack>
          <Box>
            <Button variant="contained" onClick={() => void saveBranding()} disabled={busy !== null || !accentValid}>
              Save branding
            </Button>
          </Box>
        </Stack>
      </SectionCard>

      <ClientSettingsSection client={client} onSaved={(next) => { onChange(next); setToast("Settings saved"); }} />

      <SectionCard eyebrow="Portal" title="Features">
        <FormControlLabel
          control={
            <Switch
              checked={client.features.documents !== false}
              onChange={(event) => void toggleDocuments(event.target.checked)}
              disabled={busy !== null}
            />
          }
          label="Documents — shared files between BKDP and the client"
        />
      </SectionCard>

      <SectionCard eyebrow="Identity" title="Single sign-on">
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {client.ssoProvider
            ? `${client.ssoProvider} for ${client.ssoEmailDomains.join(", ") || "no domains"}${client.ssoAutoProvision ? " · auto-provisioning on" : ""}`
            : "Not configured. Members sign in with the email invitation. SSO with the client's identity provider can be attached later without changing the portal."}
        </Typography>
      </SectionCard>

      <Snackbar open={Boolean(toast)} autoHideDuration={3000} onClose={() => setToast(null)} message={toast} />
    </Stack>
  );
};
